import { useEffect, useRef, useState } from "react";
import { MessageCircle, Phone, Mail, X, Plus } from "lucide-react";
import { AnimatePresence, motion } from "framer-motion";
import { FLOATING } from "@/constants/testIds";
import { CONTACT_INFO } from "@/data/anabiya";

/**
 * Single luxury floating action button.
 * Tap opens a compact menu: WhatsApp, Call, Email.
 * Positioned so it never overlaps the mobile sticky purchase bar.
 */
export const FloatingButtons = () => {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    if (!open) return;
    const onDoc = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    const onKey = (e) => e.key === "Escape" && setOpen(false);
    document.addEventListener("mousedown", onDoc);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDoc);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  const wa = `https://wa.me/${CONTACT_INFO.whatsapp}?text=${encodeURIComponent(
    "Hi Anabiya Decor — I would like to enquire."
  )}`;
  const tel = `tel:${CONTACT_INFO.phoneRaw}`;
  const mailto = `mailto:${CONTACT_INFO.email || "hello@anabiyadecor.com"}`;

  const items = [
    { key: "whatsapp", href: wa, label: "WhatsApp", sub: "Chat with us", icon: MessageCircle, external: true, testid: FLOATING.whatsapp },
    { key: "call", href: tel, label: "Call", sub: CONTACT_INFO.phone || "+91 91374 62428", icon: Phone, testid: FLOATING.call },
    { key: "email", href: mailto, label: "Email", sub: CONTACT_INFO.email || "hello@anabiyadecor.com", icon: Mail, testid: "floating-email" },
  ];

  return (
    <div
      ref={ref}
      className="fixed z-[60] right-5 bottom-5 md:right-6 md:bottom-6 flex flex-col items-end gap-3"
      style={{ paddingBottom: "env(safe-area-inset-bottom, 0)" }}
    >
      <AnimatePresence>
        {open && (
          <motion.ul
            key="menu"
            initial={{ opacity: 0, y: 8, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 8, scale: 0.95 }}
            transition={{ duration: 0.18 }}
            className="flex flex-col gap-2"
            data-testid="floating-menu"
          >
            {items.map((it, i) => (
              <motion.li
                key={it.key}
                initial={{ opacity: 0, x: 10 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.03 * (items.length - i) }}
              >
                <a
                  href={it.href}
                  target={it.external ? "_blank" : undefined}
                  rel={it.external ? "noopener noreferrer" : undefined}
                  data-testid={it.testid}
                  onClick={() => setOpen(false)}
                  className="group flex items-center gap-3 pl-4 pr-2 py-2 bg-white text-neutral-900 rounded-full shadow-[0_10px_32px_-8px_rgba(0,0,0,0.35)] border border-black/5 hover:bg-neutral-50 transition-colors"
                >
                  <span className="hidden sm:flex flex-col leading-tight text-right pr-1">
                    <span className="text-[11px] font-semibold uppercase tracking-[2px]">{it.label}</span>
                    <span className="text-[10px] text-neutral-500">{it.sub}</span>
                  </span>
                  <span className="w-10 h-10 rounded-full bg-neutral-900 text-white flex items-center justify-center">
                    <it.icon size={16} strokeWidth={1.8} />
                  </span>
                </a>
              </motion.li>
            ))}
          </motion.ul>
        )}
      </AnimatePresence>

      <button
        type="button"
        aria-label={open ? "Close contact menu" : "Open contact menu"}
        aria-expanded={open}
        onClick={() => setOpen((v) => !v)}
        data-testid="floating-menu-toggle"
        className="w-14 h-14 rounded-full bg-black text-white flex items-center justify-center shadow-[0_10px_32px_-8px_rgba(0,0,0,0.5)] hover:scale-[1.04] active:scale-95 transition-transform duration-300"
      >
        <span className="relative w-5 h-5 grid place-items-center">
          <AnimatePresence mode="wait" initial={false}>
            {open ? (
              <motion.span key="x" initial={{ rotate: -45, opacity: 0 }} animate={{ rotate: 0, opacity: 1 }} exit={{ rotate: 45, opacity: 0 }} transition={{ duration: 0.15 }}>
                <X size={18} strokeWidth={2} />
              </motion.span>
            ) : (
              <motion.span key="p" initial={{ rotate: 45, opacity: 0 }} animate={{ rotate: 0, opacity: 1 }} exit={{ rotate: -45, opacity: 0 }} transition={{ duration: 0.15 }}>
                <Plus size={20} strokeWidth={2} />
              </motion.span>
            )}
          </AnimatePresence>
        </span>
      </button>
    </div>
  );
};

export default FloatingButtons;
